import { Alert, Button, KeyRoundIcon, LockIcon, LogInIcon, MailIcon, TextField } from "@strust/ui";
import { css } from "@strust/ui/css";
import type { SyntheticEvent } from "react";
import { resetFields, submitFields, useFormField } from "../../../shared/form-field.ts";
import { useIntl } from "react-intl";
import { loginMessages, passwordRevealMessages } from "../messages.ts";

const FORM = css({ display: "flex", flexDirection: "column", gap: "4" });
const FULL = css({ width: "full" });
const DIVIDER = css({
  display: "flex",
  alignItems: "center",
  gap: "3",
  textStyle: "overline",
  color: "text.2",
  _before: { content: '""', flex: "1", height: "1px", backgroundColor: "line" },
  _after: { content: '""', flex: "1", height: "1px", backgroundColor: "line" },
});

export interface LoginFormProps {
  readonly onSubmit: (credentials: { email: string; password: string }) => void;
  readonly onSso: (email: string) => void;
  readonly pending: boolean;
  /** Text of the last refusal (already translated), shown above the form. */
  readonly error?: string | undefined;
  /** Informational text (session ended, signed out). */
  readonly info?: string | undefined;
  /** The refusal asked the user to use SSO: the button is emphasized. */
  readonly preferSso?: boolean;
}

/** Email and password form plus the SSO button; presentational, no fetching. */
export function LoginForm({ onSubmit, onSso, pending, error, info, preferSso }: LoginFormProps) {
  const intl = useIntl();
  const email = useFormField({
    validate: (value) =>
      value.trim() === "" ? intl.formatMessage(loginMessages.emailRequired) : undefined,
  });
  const password = useFormField({
    validate: (value) =>
      value === "" ? intl.formatMessage(loginMessages.passwordRequired) : undefined,
  });

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!submitFields(email, password)) return;
    onSubmit({ email: email.value.trim(), password: password.value });
    // the password never lingers in the form after an attempt
    resetFields(password);
  };

  return (
    <form onSubmit={submit} noValidate className={FORM}>
      {info ? <Alert>{info}</Alert> : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <TextField
        label={intl.formatMessage(loginMessages.email)}
        type="email"
        name="email"
        leadingIcon={<MailIcon />}
        autoComplete="username"
        {...email.inputProps}
      />
      <TextField
        label={intl.formatMessage(loginMessages.password)}
        type="password"
        name="password"
        leadingIcon={<LockIcon />}
        revealLabels={{
          show: intl.formatMessage(passwordRevealMessages.show),
          hide: intl.formatMessage(passwordRevealMessages.hide),
        }}
        autoComplete="current-password"
        {...password.inputProps}
      />
      <Button
        type="submit"
        className={FULL}
        visual={preferSso ? "secondary" : "primary"}
        icon={<LogInIcon />}
        loading={pending}
      >
        {intl.formatMessage(loginMessages.submit)}
      </Button>
      <div aria-hidden="true" className={DIVIDER}>
        {intl.formatMessage(loginMessages.or)}
      </div>
      <Button
        className={FULL}
        visual={preferSso ? "primary" : "secondary"}
        icon={<KeyRoundIcon />}
        loading={pending}
        onClick={() => {
          if (!submitFields(email)) return;
          onSso(email.value.trim());
        }}
      >
        {intl.formatMessage(loginMessages.sso)}
      </Button>
    </form>
  );
}
